/**
 * The clock the page counts its own character's timers on (ADR-056's amendment "a frozen character is safe", decision
 * 39, and its note of 2026-10-08, point 10). The map server stops a held character's clock: its effects, its cooldowns,
 * its Combatant window and its flag switch keep the ticks they had, through the hold and the grace after it. The page
 * counts those down between frames on its own clock, so it reads them on this one, which stands still while the page's
 * own character reads held (`Away.Held`) and runs on from where it stood once it no longer does. A time the page was
 * told before the hold, during it or at its end therefore has the ticks left the map server gave it, and nothing counts
 * down and jumps back.
 */

/**
 * A clock that stands still through a hold.
 *
 * @example
 * const clock = new HeldClock();
 * clock.hold(true, performance.now());
 * clock.read(performance.now());
 */
export class HeldClock {
  private heldAt: number | null = null;
  private paused = 0;

  /**
   * Whether the clock stands still now.
   *
   * @returns True through a hold.
   */
  public get held(): boolean {
    return this.heldAt !== null;
  }

  /**
   * Tells the clock whether the own character reads held. Telling it the state it is already in changes nothing.
   *
   * @param held Whether the character reads held.
   * @param nowMs The page clock, in ms.
   */
  public hold(held: boolean, nowMs: number): void {
    if (held && this.heldAt === null) {
      this.heldAt = nowMs;
    } else if (!held && this.heldAt !== null) {
      this.paused += Math.max(0, nowMs - this.heldAt);
      this.heldAt = null;
    }
  }

  /**
   * The own character's time at a moment of the page clock: the page clock less every hold so far, and the moment the
   * hold began while one stands.
   *
   * @param nowMs The page clock, in ms.
   * @returns The own character's time, in ms.
   */
  public read(nowMs: number): number {
    return (this.heldAt ?? nowMs) - this.paused;
  }
}

/**
 * The page's own clock: told of the hold as each snapshot is applied, and read wherever the page counts its own
 * character's timers.
 */
export const ownClock = new HeldClock();

/**
 * The own character's time now.
 *
 * @returns `ownClock` read at the page clock's now, in ms.
 */
export function ownNow(): number {
  return ownClock.read(performance.now());
}
